import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkle, Clock, Wrench, CurrencyDollar, Warning, FileText } from "@phosphor-icons/react";

export default function EstimatorModal({ initialTitle = "", initialDescription = "", jobId = null, onClose, onApplied }) {
  const [f, setF] = useState({ title: initialTitle, description: initialDescription, trade: "", labor_rate: 165 });
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [est, setEst] = useState(null);
  const [selected, setSelected] = useState({});  // partIndex -> true when included

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (e) => {
    if (e) e.preventDefault();
    if (!f.title.trim()) { toast.error("Give the job a title first"); return; }
    setBusy(true); setEst(null);
    try {
      const { data } = await api.post("/ai/estimate", {
        title: f.title.trim(),
        description: f.description.trim(),
        trade: f.trade.trim() || null,
        labor_rate: Number(f.labor_rate) || 165,
      });
      if (!data.ok) {
        toast.error("AI couldn't produce a clean estimate — try adding more description");
        return;
      }
      setEst(data);
      // default: all parts selected
      const s = {};
      (data.parts || []).forEach((_, i) => { s[i] = true; });
      setSelected(s);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Estimator failed");
    } finally { setBusy(false); }
  };

  const applyToJob = async () => {
    if (!jobId || !est) return;
    const parts = (est.parts || []).filter((_, i) => selected[i]);
    if (parts.length === 0) { toast.error("Select at least one part to add"); return; }
    setApplying(true);
    try {
      await api.post("/ai/estimate/apply", {
        job_id: jobId,
        parts,
        labor_hours: est.hours_typical || 0,
        labor_rate: est.labor_rate || 165,
      });
      toast.success(`Added ${parts.length} part${parts.length === 1 ? "" : "s"} to the job`);
      if (onApplied) onApplied();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Apply failed");
    } finally { setApplying(false); }
  };

  const selectedParts = () => (est?.parts || []).filter((_, i) => selected[i]);

  const saveToJob = async () => {
    if (!jobId || !est) return;
    setApplying(true);
    try {
      await api.post(`/jobs/${jobId}/ai-estimate/save`, { estimate: est });
      toast.success("Estimate saved on the WO");
      if (onApplied) onApplied();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setApplying(false); }
  };

  const toProposal = async () => {
    if (!jobId || !est) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/quotes/from-estimate`, { estimate: est, parts: selectedParts() });
      toast.success(`Proposal drafted ($${((data.subtotal_cents || 0) / 100).toFixed(0)}) — flagged Needs Review`);
      if (onApplied) onApplied();
      onClose();
      window.dispatchEvent(new CustomEvent("fr:quote-created", { detail: data }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create proposal");
    } finally { setApplying(false); }
  };

  const selectedTotal = (est?.parts || []).reduce((s, p, i) => selected[i] ? s + (p.line_total || 0) : s, 0);
  const grandLow = (est ? est.labor_low + selectedTotal : 0);
  const grandHigh = (est ? est.labor_high + selectedTotal : 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a]" onClick={e => e.stopPropagation()} data-testid="estimator-modal">
        <div className="sticky top-0 bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white p-5 flex items-center justify-between border-b-2 border-[#0f172a]">
          <div className="flex items-center gap-2">
            <Sparkle weight="fill" size={22}/>
            <h3 className="font-display font-black text-2xl uppercase tracking-tight">AI Job Estimator</h3>
          </div>
          <button onClick={onClose} data-testid="estimator-close"><X weight="bold" size={22}/></button>
        </div>

        <div className="p-5 space-y-3">
          <form onSubmit={run} className="space-y-3">
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Job Title <span className="text-red-500">*</span></label>
              <input required value={f.title} onChange={upd("title")} data-testid="est-title" placeholder="e.g. Replace leaking kitchen faucet"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Description (optional)</label>
              <textarea value={f.description} onChange={upd("description")} rows={2} data-testid="est-desc"
                placeholder="Tenant reports slow drip under sink; single-hole faucet with sprayer."
                className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a]"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wider">Trade (optional)</label>
                <input value={f.trade} onChange={upd("trade")} data-testid="est-trade" placeholder="plumbing, electrical, HVAC…"
                  className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider">Labor rate $/hr</label>
                <input type="number" min="0" value={f.labor_rate} onChange={upd("labor_rate")} data-testid="est-rate"
                  className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="est-run"
              className="tactile-btn w-full h-12 bg-[#0f172a] text-white font-black uppercase tracking-wider disabled:opacity-50">
              {busy ? "AI is estimating…" : est ? "Re-estimate" : "Generate Estimate"}
            </button>
          </form>

          {est && (
            <div className="space-y-3 pt-3 border-t-2 border-slate-200" data-testid="estimator-result">
              {/* Hours + Labor */}
              <div className="grid grid-cols-3 gap-3">
                <StatBlock icon={<Clock weight="bold" size={20}/>} label="Hours" value={`${est.hours_low}–${est.hours_high}`} sub={`typical ${est.hours_typical}h`}/>
                <StatBlock icon={<Wrench weight="bold" size={20}/>} label="Labor" value={`$${est.labor_low.toFixed(0)}–$${est.labor_high.toFixed(0)}`} sub={`@ $${est.labor_rate}/hr`}/>
                <StatBlock icon={<CurrencyDollar weight="bold" size={20}/>} label="Estimated Total" value={`$${grandLow.toFixed(0)}–$${grandHigh.toFixed(0)}`} sub={est.confidence.toUpperCase() + " confidence"}/>
              </div>

              {est.notes && (
                <div className="rugged-card p-3 bg-amber-50 border-amber-500 text-sm" data-testid="est-notes">
                  <div className="flex items-start gap-2">
                    <Warning weight="fill" size={16} className="text-amber-700 mt-0.5 flex-shrink-0"/>
                    <span className="text-amber-900"><b className="uppercase text-xs tracking-wider">Assumptions:</b> {est.notes}</span>
                  </div>
                </div>
              )}

              {/* Parts list */}
              <div>
                <h4 className="font-display font-black text-sm uppercase tracking-wider mb-2">Suggested Parts</h4>
                {(est.parts || []).length === 0 ? (
                  <div className="text-sm text-slate-500 font-medium italic">No parts needed for this job.</div>
                ) : (
                  <div className="border-2 border-[#0f172a] divide-y divide-slate-200" data-testid="est-parts-list">
                    {est.parts.map((p, i) => (
                      <label key={i} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50" data-testid={`est-part-${i}`}>
                        <input type="checkbox" checked={!!selected[i]} onChange={() => setSelected({ ...selected, [i]: !selected[i] })}
                          className="mt-1 h-4 w-4"/>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-black">{p.name}</span>
                            {p.sku && <span className="text-[10px] font-mono text-slate-500">{p.sku}</span>}
                            {p.source === "ai_suggested_missing" && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 font-black uppercase">not in catalog</span>
                            )}
                          </div>
                          {p.reason && <div className="text-xs text-slate-600 mt-0.5">{p.reason}</div>}
                        </div>
                        <div className="text-right whitespace-nowrap font-mono text-sm">
                          <div>{p.quantity}× ${p.sell_price.toFixed(2)}</div>
                          <div className="font-black text-[#0f172a]">${p.line_total.toFixed(2)}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {jobId && (
                <div className="space-y-2">
                  <button onClick={toProposal} disabled={applying} data-testid="est-to-proposal"
                    className="tactile-btn w-full h-12 bg-[#0f172a] text-white font-black uppercase tracking-wider disabled:opacity-50 inline-flex items-center justify-center gap-2">
                    <FileText weight="fill" size={16}/> {applying ? "Creating…" : "Turn into Proposal → Needs Review"}
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={saveToJob} disabled={applying} data-testid="est-save"
                      className="tactile-btn h-11 border-2 border-[#0f172a] bg-white text-[#0f172a] font-black uppercase text-xs tracking-wider disabled:opacity-50">
                      Save Estimate to WO
                    </button>
                    <button onClick={applyToJob} disabled={applying || (est.parts || []).length === 0} data-testid="est-apply"
                      className="tactile-btn h-11 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider disabled:opacity-50">
                      {(est.parts || []).length === 0 ? "No parts to add" : `Add ${Object.values(selected).filter(Boolean).length} Part(s)`}
                    </button>
                  </div>
                </div>
              )}
              {!jobId && (
                <div className="text-xs text-slate-500 font-medium text-center italic">
                  Save the job first to add these parts to it directly.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBlock({ icon, label, value, sub }) {
  return (
    <div className="rugged-card p-3">
      <div className="flex items-center gap-2 text-slate-500">
        {icon}
        <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
      </div>
      <div className="mt-1 font-display font-black text-xl leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{sub}</div>}
    </div>
  );
}
